import React from "react";
import PropTypes from "prop-types";
import ProgressBar from "./ProgressBar";
import styles from "./LoyaltyCardItem.module.css";

/**
 * Item da lista de cartões de fidelidade.
 *
 * Sem ações destrutivas/manuais (o briefing proíbe edição manual de
 * `visitCount`) — apenas visualização.
 */

const MS_DAY = 24 * 60 * 60 * 1000;

/**
 * Formata data relativa em pt-BR ("há 3 dias", "há 2 semanas", "hoje").
 * Implementação inline para evitar adicionar dependência (date-fns, dayjs)
 * só por causa de uma única tela.
 */
function formatRelative(dateStr) {
  if (!dateStr) return "Sem visitas registradas";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "Data desconhecida";
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return "Em breve";
  const diffDays = Math.floor(diffMs / MS_DAY);
  if (diffDays === 0) return "hoje";
  if (diffDays === 1) return "ontem";
  if (diffDays < 7) return `há ${diffDays} dias`;
  if (diffDays < 14) return "há 1 semana";
  if (diffDays < 30) return `há ${Math.floor(diffDays / 7)} semanas`;
  if (diffDays < 60) return "há 1 mês";
  if (diffDays < 365) return `há ${Math.floor(diffDays / 30)} meses`;
  if (diffDays < 730) return "há 1 ano";
  return `há ${Math.floor(diffDays / 365)} anos`;
}

/** Inicial do nome (ou do telefone) para avatar. */
function getInitial(card) {
  if (card.clientName?.trim()) {
    return card.clientName.trim().charAt(0);
  }
  if (card.clientPhone?.trim()) {
    // Pula caracteres não-alfanuméricos para chegar no primeiro dígito real
    const m = card.clientPhone.match(/[0-9a-zA-ZÀ-ÿ]/);
    return m ? m[0] : "?";
  }
  return "?";
}

/** Telefone formatado para display + para tel: URI. */
function formatPhoneForDisplay(phone) {
  if (!phone) return "";
  // Formato brasileiro: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
  // Aceita números de 10 ou 11 dígitos (com DDD).
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

function formatPhoneForTel(phone) {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  // tel: URI aceita "+55..." (DDI). Se já tiver DDI, mantém.
  if (digits.length === 13 && digits.startsWith("55")) return `+${digits}`;
  if (digits.length === 11 || digits.length === 10) return `+55${digits}`;
  return phone;
}

export default function LoyaltyCardItem({ card, requiredVisits }) {
  const displayName = card.clientName?.trim() || formatPhoneForDisplay(card.clientPhone);
  const phoneDisplay = formatPhoneForDisplay(card.clientPhone);
  const phoneTel = formatPhoneForTel(card.clientPhone);
  const visitsLeft = Math.max(requiredVisits - card.visitCount, 0);
  const isWinner = card.visitCount >= requiredVisits && requiredVisits > 0;

  return (
    <li className={styles.item}>
      <div className={styles.avatar} aria-hidden="true">
        {getInitial(card)}
      </div>
      <div className={styles.body}>
        <div className={styles.headerRow}>
          <p className={styles.name} title={displayName}>
            {displayName}
          </p>
          {card.clientPhone && card.clientName && (
            <a
              href={`tel:${phoneTel}`}
              className={styles.phone}
              aria-label={`Ligar para ${displayName}`}
            >
              📱 {phoneDisplay}
            </a>
          )}
        </div>

        <ProgressBar
          value={card.visitCount}
          max={requiredVisits}
          ariaLabel={`${card.visitCount} de ${requiredVisits} cortes`}
        />

        <p
          className={`${styles.progressText} ${isWinner ? styles.progressTextWinner : ""}`.trim()}
        >
          {isWinner
            ? "🎉 Prêmio disponível!"
            : `${card.visitCount} de ${requiredVisits} cortes — Falta${
                visitsLeft === 1 ? "" : "m"
              } ${visitsLeft} para o prêmio!`}
        </p>

        <div className={styles.metaRow}>
          {card.rewardsEarned > 0 && (
            <>
              <span className={styles.rewardsTag}>
                🏆 {card.rewardsEarned}{" "}
                {card.rewardsEarned === 1 ? "recompensa conquistada" : "recompensas conquistadas"}
              </span>
              <span className={styles.dot} aria-hidden="true">
                •
              </span>
            </>
          )}
          <span>Última visita: {formatRelative(card.lastVisitAt)}</span>
        </div>
      </div>
    </li>
  );
}

LoyaltyCardItem.propTypes = {
  card: PropTypes.shape({
    id: PropTypes.string,
    clientName: PropTypes.string,
    clientPhone: PropTypes.string,
    visitCount: PropTypes.number.isRequired,
    rewardsEarned: PropTypes.number,
    lastVisitAt: PropTypes.string,
  }).isRequired,
  requiredVisits: PropTypes.number.isRequired,
};
